import * as React from "react"
import { Menu as BaseMenu } from "@base-ui/react/menu"
import { Check, ChevronRight } from "lucide-react"
import { cn } from "@lib/cn"

const Root = BaseMenu.Root
const Trigger = BaseMenu.Trigger
const Portal = BaseMenu.Portal
const Group = BaseMenu.Group
const RadioGroup = BaseMenu.RadioGroup
const SubRoot = BaseMenu.SubmenuRoot

const popupClass = cn(
  "min-w-[12rem] overflow-hidden rounded-md border p-1",
  "bg-white text-cloverhound-gray-900 dark:bg-cloverhound-gray-900 dark:text-cloverhound-gray-50",
  "border-cloverhound-gray-250 dark:border-cloverhound-gray-800",
  "shadow-lg",
  "data-[starting-style]:opacity-0 data-[starting-style]:scale-95",
  "data-[ending-style]:opacity-0 data-[ending-style]:scale-95",
  "transition-all duration-quick ease-out-quad",
)

const itemClass = cn(
  "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5",
  "text-small outline-none",
  "data-[highlighted]:bg-cloverhound-gray-100 dark:data-[highlighted]:bg-cloverhound-gray-800",
  "data-[disabled]:opacity-50 data-[disabled]:pointer-events-none",
)

const Content = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseMenu.Popup> & {
    sideOffset?: number
    align?: "start" | "center" | "end"
    side?: "top" | "right" | "bottom" | "left"
  }
>(({ className, sideOffset = 6, align = "start", side = "bottom", children, ...props }, ref) => (
  <BaseMenu.Portal>
    <BaseMenu.Positioner
      className="z-50"
      sideOffset={sideOffset}
      align={align}
      side={side}
    >
      <BaseMenu.Popup ref={ref} className={cn(popupClass, className)} {...props}>
        {children}
      </BaseMenu.Popup>
    </BaseMenu.Positioner>
  </BaseMenu.Portal>
))
Content.displayName = "Menu.Content"

const Item = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseMenu.Item>
>(({ className, ...props }, ref) => (
  <BaseMenu.Item ref={ref} className={cn(itemClass, className)} {...props} />
))
Item.displayName = "Menu.Item"

const LinkItem = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<typeof BaseMenu.LinkItem>
>(({ className, ...props }, ref) => (
  <BaseMenu.LinkItem ref={ref} className={cn(itemClass, className)} {...props} />
))
LinkItem.displayName = "Menu.LinkItem"

const CheckboxItem = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseMenu.CheckboxItem>
>(({ className, children, ...props }, ref) => (
  <BaseMenu.CheckboxItem ref={ref} className={cn(itemClass, "pl-7", className)} {...props}>
    <span className="absolute left-1.5 flex size-4 items-center justify-center">
      <BaseMenu.CheckboxItemIndicator>
        <Check className="size-3.5" aria-hidden="true" />
      </BaseMenu.CheckboxItemIndicator>
    </span>
    {children}
  </BaseMenu.CheckboxItem>
))
CheckboxItem.displayName = "Menu.CheckboxItem"

const RadioItem = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseMenu.RadioItem>
>(({ className, children, ...props }, ref) => (
  <BaseMenu.RadioItem ref={ref} className={cn(itemClass, "pl-7", className)} {...props}>
    <span className="absolute left-1.5 flex size-4 items-center justify-center">
      <BaseMenu.RadioItemIndicator>
        <span className="size-2 rounded-full bg-current" />
      </BaseMenu.RadioItemIndicator>
    </span>
    {children}
  </BaseMenu.RadioItem>
))
RadioItem.displayName = "Menu.RadioItem"

const Label = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("px-2 py-1.5 text-micro font-semibold uppercase tracking-wide text-cloverhound-gray-600", className)}
      {...props}
    />
  ),
)
Label.displayName = "Menu.Label"

const Separator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      role="separator"
      className={cn("-mx-1 my-1 h-px bg-cloverhound-gray-200 dark:bg-cloverhound-gray-800", className)}
      {...props}
    />
  ),
)
Separator.displayName = "Menu.Separator"

const Shortcut = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn("ml-auto text-mini tracking-widest text-cloverhound-gray-600", className)}
      {...props}
    />
  ),
)
Shortcut.displayName = "Menu.Shortcut"

const SubTrigger = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseMenu.SubmenuTrigger>
>(({ className, children, ...props }, ref) => (
  <BaseMenu.SubmenuTrigger ref={ref} className={cn(itemClass, className)} {...props}>
    {children}
    <ChevronRight className="ml-auto size-3.5" aria-hidden="true" />
  </BaseMenu.SubmenuTrigger>
))
SubTrigger.displayName = "Menu.SubTrigger"

export const Menu = {
  Root,
  Trigger,
  Portal,
  Content,
  Item,
  LinkItem,
  CheckboxItem,
  RadioItem,
  RadioGroup,
  Group,
  Label,
  Separator,
  Shortcut,
  Sub: SubRoot,
  SubTrigger,
  SubContent: Content,
}
